import { t } from 'ttag'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useContext } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'

import { api } from '@h2/api'
import { Globals } from '@h2/globals'
import { Modal } from '@h2/components/containers/modal'
import { Flex } from '@h2/components/layout/flex'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Button } from '@h2/components/buttons/button'
import { Spinner } from '@h2/components/loading/spinner'
import { PG, PB97T } from '@h2/components/colors'
import { lists } from '@h2/components/mixins'
import { features } from '@h2/components/media'
import approvalSvg from '@h2/assets/svg/illustrations/approvalNeeded.svg'

import { Link } from './link'

import { showUserFeedback } from '../actions/showUserFeedback'

const FlexHeader = styled(Flex)`
  @media (${features.minWidth.laptop}) {
    flex-direction: row;

    > h3 {
      text-align: left;
    }
  }
`
const TextWrap = styled.div`
  @media (${features.minWidth.laptop}) {
    padding-top: 30px;

    > p {
      text-align: left;
    }
  }
`
const ResendText = styled(Text)`
  button {
    font-size: 16px;
  }
`
const Admins = styled.ul`
  ${lists};
  max-height: 200px;
  overflow: hidden auto;
  margin-top: 15px;

  @media (${features.minWidth.laptop}) {
    max-height: 300px;
  }
`
const Admin = styled.li`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 0;

  &:nth-child(n + 2) {
    border-top: 1px solid ${PB97T};
  }

  a {
    align-self: flex-start;
  }

  @media (${features.minWidth.laptop}) {
    flex-direction: row;
    align-items: center;

    span {
      width: 40%;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    span:nth-child(2) {
      flex-grow: 1;
    }

    a {
      width: 100px;
      align-self: flex-start;
    }
  }
`
const EmailConfirmationDialog = ({ onClose }) => {
  const dispatch = useDispatch()
  const { isSubscriber, systemName, user } = useContext(Globals)
  const { data: administrators, isLoading } = useQuery('administrators', async () => {
    if (isSubscriber) {
      const admins = await api.fetch('/siteConfig/administrators')

      if (Array.isArray(admins) && admins.length === 0) {
        admins.push({
          firstName: 'Headed2',
          lastName: 'Onboarding',
          emailAddress: 'support@headed2.com'
        })
      }

      return admins
    }

    return []
  })

  if (isLoading) {
    return (
      <Modal isOpen onClose={onClose}>
        <Modal.Header>
          <Flex padding="50px 0" alignItems="center" justifyContent="center">
            <Spinner color={PG} size="large" />
          </Flex>
        </Modal.Header>
      </Modal>
    )
  }

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Header>
        <FlexHeader direction="column" alignItems="center" gap="15px">
          <img src={approvalSvg} alt="" width="192px" height="129px" />
          <Heading level="3" weight="light" variant="mixed-weight" align="center" mb="0">
            <span>{user.userDisplayName}</span>
            {t`, your`} <span>{systemName}</span>{' '}
            {isSubscriber
              ? t`access is limited until an administrator approves your account.`
              : t`access is limited until you confirm your email address.`}
          </Heading>
        </FlexHeader>
      </Modal.Header>
      <Modal.Body>
        <TextWrap>
          <Text align="center">
            {t`Thank you for registering on the`} <strong>{systemName}</strong>{' '}
            {t`as a Navigator. To access all of the Naviagtor functionality,`}{' '}
            {isSubscriber
              ? t`you first need approval from an administrator. If you haven't already, you should contact one of the administrators below:`
              : t`you first need to confirm your email address. You should have received a confirmation email to the address you entered.`}
          </Text>
          {!isSubscriber && (
            <ResendText align="center">
              {t`If you didn't receive the email,`}{' '}
              <Button
                variant="link"
                onClick={async () => {
                  try {
                    await api.fetch('/auth/validation/send')
                    dispatch(
                      showUserFeedback(
                        t`Confirmation email sent. Check the email address used during registration.`,
                        'info'
                      )
                    )
                  } catch {
                    dispatch(
                      showUserFeedback(
                        t`Could not resend confirmation email. Please try again later.`,
                        'error'
                      )
                    )
                  } finally {
                    onClose()
                  }
                }}>
                {t`resend here`}
              </Button>
              .
            </ResendText>
          )}
        </TextWrap>
        {isSubscriber && (
          <Admins>
            {administrators.map(admin => (
              <Admin key={admin.emailAddress}>
                <Text weight="bold" as="span">
                  {admin.firstName} {admin.lastName}
                </Text>
                <Text weight="bold" as="span">
                  {admin.emailAddress}
                </Text>
                <Link
                  ssr
                  href={`mailto:${admin.emailAddress}?subject=${encodeURIComponent(
                    `Navigator access to ${systemName}`
                  )}`}
                  variant="outlined-cta">
                  {t`Contact`}
                </Link>
              </Admin>
            ))}
          </Admins>
        )}
      </Modal.Body>
    </Modal>
  )
}

EmailConfirmationDialog.propTypes = {
  onClose: PropTypes.func
}

export { EmailConfirmationDialog }
